// Tema del campus · claro / oscuro, elegido por el usuario y recordado.
//
// Default OSCURO (experiencia actual probada). La clase se aplica en <html>
// (.theme-dark / .theme-light) y los tokens CSS (ver index.css) hacen el resto.
// Un script inline en index.html aplica la clase ANTES de pintar (anti-flash);
// este provider la mantiene sincronizada y expone el toggle.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'robotea-theme';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Lee el tema actual del <html> (lo puso el script inline) o de storage. */
function readInitialTheme(): Theme {
  if (typeof document !== 'undefined') {
    if (document.documentElement.classList.contains('theme-light')) return 'light';
    if (document.documentElement.classList.contains('theme-dark')) return 'dark';
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* storage no disponible · default dark */
  }
  return 'dark';
}

function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  el.classList.remove('theme-dark', 'theme-light');
  el.classList.add(theme === 'light' ? 'theme-light' : 'theme-dark');
  // theme-color de la barra del navegador (PWA/móvil).
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'light' ? '#EEF1F5' : '#0B0F1A');
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage no disponible · solo aplica en runtime */
    }
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo(() => ({ theme, toggle, setTheme }), [theme, toggle, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback seguro si se usa fuera del provider (no debería): asume dark,
    // no-op en toggle. Evita crash.
    return { theme: 'dark', toggle: () => {}, setTheme: () => {} };
  }
  return ctx;
}
